import React, {useEffect, useRef} from "react";
import {useNavigate} from "react-router-dom";
import _, {isNumber} from "lodash";
import {isMobile} from "../../../App";
import paths from "../../../paths";
import {auth} from "../../../store/auth.store";
import {BettingTypesMap} from "features/core/definitions";
import {CurrencyFormat, dateTimeStringFormat, preventBetSlipValues} from "../../../services/misc.functions";
import {modalServiceSubject} from "../../../services/modal.service";
import {
    FOBBetslip,
    FobSlipItem,
    getFobItemDisplayOdds,
    isFobItemPlayable,
    isFobPendingPickPlayable,
    resolveFobItemHorses,
    useFobBetslip,
    useFobPendingPicks,
    useFobSlipOptions,
} from "features/betslip/fob.service";
import {CouponImage, resolveCouponImage, startCouponImageCapture} from "../coupon-image";
import "../../../shared/betslip/betslip.css";
import iconTrash from "../../../assets/feather/trash-2.svg";
import iconSave from "../../../assets/feather/save.svg";
import iconClose from "../../../assets/feather/x-circle.svg";
import iconLeft from "../../../assets/feather/chevron-left.svg";
import infoDangerIcon from "../../../assets/feather/info-danger.svg";
import jokeyIcon from "../../../assets/img/jokey.png";
import blackHorse from "../../../assets/img/siyahat.png";

const BET_SLIP_MAX_MULTIPLY = 12000;
const MAX_WIN_ERROR = "Maksimum kazanç tutarı 7.500.000 ₺'den fazla olamaz";

function buildMultiplyChoices(): number[] {
    let choices: number[] = [];
    if (BET_SLIP_MAX_MULTIPLY > 200) {
        choices = _.range(1, 101);
        const mod = BET_SLIP_MAX_MULTIPLY % 50;
        choices.push(..._.range(200, BET_SLIP_MAX_MULTIPLY, 50));
        if (mod > 0) {
            choices.push(choices[choices.length - 1] + mod);
        }
    } else {
        choices = _.range(1, BET_SLIP_MAX_MULTIPLY + 1);
    }
    return choices;
}

const multiplyChoices = buildMultiplyChoices();

function buildConfirmModalContent(slipItem: FobSlipItem[], totalAmount: number, minWin: number, maxWin: number, isClosing: boolean, hasKnownOdds: boolean, fobSystemSelected: number[]): string {
    const legsHtml = slipItem
        .map((item) => {
            const {isPair, primaryHorse, secondHorse, label} = resolveFobItemHorses(item);
            const displayName = isPair
                ? `${primaryHorse?.name ?? ''}${BettingTypesMap[item.bettingType]?.isHeadToHead ? '<br/> vs ' : '-'}${secondHorse?.name ?? ''}`
                : (primaryHorse?.name ?? '');
            const odds = getFobItemDisplayOdds(item);
            const width = slipItem.length < 4 ? `calc(100% / ${slipItem.length})` : '25%';

            return `<div class="col-12 px-2 my-2" style="width: ${isMobile ? '' : width}; flex: unset; max-width: unset;">
                <div class="run-no">${item.hippodrome.hippodrome} ${item.run.no}. Koşu</div>
                <div class="horse-no justify-content-between align-items-center d-flex">
                    <div style="font-weight: bold">${label} - ${displayName}</div>
                    <div class="text-left ps-2">${odds}</div>
                </div>
            </div>`;
        })
        .join('');

    const hasSystem = fobSystemSelected.length > 0;
    const summaryColClass = hasSystem ? 'col-4' : 'col-6';

    return `
        <div class="coupon-legs" style="width:auto; margin-top: 15px;">${legsHtml}</div>
        <div class="row">
            <div class="col-12">
                <p class="my-1 alert alert-warning p-2 fs-7 fw-bold">
                    Sabit ihtimalli bahis türünde oynanan kuponlar iptal edilemez. Bilet oynanırken tüm oranlar değişebilir.
                </p>
            </div>
            <div class="${summaryColClass} d-flex gap-2 fw-bold fs-6">
                <div>Toplam Tutar:</div><div>${CurrencyFormat(totalAmount)}₺</div>
            </div>
            ${hasSystem ? `<div class="col-4 d-flex gap-2 fw-bold fs-6">
                <div>Sistem:</div><div>${fobSystemSelected.join('-')}</div>
            </div>` : ''}
            <div class="${summaryColClass} d-flex gap-2 fw-bold fs-6">
                <div>Maks. Kazanç:</div>
                <div>${!hasKnownOdds ? 'KO' : `${hasSystem ? `${CurrencyFormat(minWin)}~` : ''}${CurrencyFormat(maxWin)}₺${isClosing ? ' + KO' : ''}`}</div>
            </div>
        </div>`;
}

export function NewFobBetslip({selectedBettingType, onClose}: any) {
    const navigate = useNavigate();
    const slipItem = useFobBetslip();
    const pendingPicks = useFobPendingPicks();
    const options = useFobSlipOptions();

    const systemOptions = FOBBetslip.getSystemOptionsCount();
    const totalAmount = FOBBetslip.getTotalAmount();
    const {minWin, maxWin, isClosing} = FOBBetslip.getMinMax();
    const totalOdds = FOBBetslip.getTotalOdds();
    const hasKnownOdds = totalOdds !== "KO";
    const allHippodromesOpen = slipItem.length > 0 && slipItem.every((item) => item.hippodrome?.show_fob);
    const hasStaleItem = slipItem.some((item) => !isFobItemPlayable(item));
    const errorClearTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

    useEffect(() => () => {
        if (errorClearTimer.current) clearTimeout(errorClearTimer.current);
    }, []);

    const scheduleErrorClear = () => {
        if (errorClearTimer.current) clearTimeout(errorClearTimer.current);
        errorClearTimer.current = setTimeout(() => FOBBetslip.setOptions({errorMessage: null}), 5000);
    };

    useEffect(() => {
        if (maxWin > 7_500_000) {
            if (options.errorMessage !== MAX_WIN_ERROR) {
                FOBBetslip.setOptions({errorMessage: MAX_WIN_ERROR});
            }
        } else if (options.errorMessage === MAX_WIN_ERROR) {
            FOBBetslip.setOptions({errorMessage: null});
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [maxWin]);

    const confirmCoupon = async () => {
        if (!(auth.authentication && auth.member && auth.member.id)) {
            navigate(`${paths.login.url}/${window.location.pathname.replaceAll("/", "|")}`);
            return;
        }

        let errorMessage: string | null = null;

        if (pendingPicks.length > 0) {
            errorMessage = "Gerekli tüm koşu/alanlardan seçim yapmadınız.";
        } else if (hasStaleItem) {
            errorMessage = "Sepetinizde artık geçerli olmayan bir seçim var, lütfen kaldırın.";
        } else if (
            options.shareCoupon !== true &&
            (options.usePointBalance
                ? Number(auth.member.balance_debit) + Number(auth.member.balance_point)
                : auth.member.balance_debit) < Number(totalAmount)
        ) {
            errorMessage = "Bakiyeniz yetersiz.";
        } else if (totalAmount < (selectedBettingType?.minCouponAmount ?? 20)) {
            errorMessage = "Minumum bahis tutarı " + CurrencyFormat(20) + " TL olmalıdır.";
        }

        if (errorMessage) {
            FOBBetslip.setOptions({errorMessage});
            scheduleErrorClear();
            return;
        }

        // Görüntü onay modalı açılmadan ÖNCE başlatılıyor (modal görüntüye girmesin) ama
        // BEKLENMİYOR: anlık görüntü çağrı anında alınıyor, geri kalanı modal açıkken bitiyor.
        const imgData = startCouponImageCapture("betslip-body");

        if (isMobile) {
            playCoupon(false, imgData);
            return;
        }

        modalServiceSubject.next({
            title: `BAHİS DETAYI - ${dateTimeStringFormat(slipItem[0]?.hippodrome?.date, true)}`,
            width: "960px",
            content: buildConfirmModalContent(slipItem, totalAmount, minWin, maxWin, isClosing, hasKnownOdds, options.fobSystemSelected),
            confirm: {
                sure: {class: "btn-success", label: "Onayla", action: () => playCoupon(false, imgData)},
                cancel: {label: "Vazgeç"},
            },
        });

        scheduleErrorClear();
    };

    const playCoupon = async (save: boolean = false, imgData: CouponImage = "") => {
        if (options.inProgress || slipItem.length === 0) return;

        if (!auth.member || !auth.member.id) {
            modalServiceSubject.next({
                title: "Üye Girişi",
                content: `<p>Bahis yapabilmeniz için önce oturum açmanız gerekli. Üye değilseniz hemen üye olabilir yada giriş yaparak bahis yapabilirsiniz.</p>`,
                confirm: {
                    sure: {label: "Giriş Yap", class: "btn btn-success", action: () => navigate(paths.login.url)},
                    cancel: {label: "Üye Ol", class: "btn btn-primary", action: () => navigate(paths.register.url)},
                },
            });
            return;
        }

        const processingModalTimeout = setTimeout(() => {
            modalServiceSubject.next({
                case: "info",
                title: "Kuponunuz Oynanıyor",
                content: '<div class="text-center"><div class="loader"></div><p>Kuponunuz oynanmak üzere United Racing sistemine iletildi. United Racing sisteminden yanıt gelene kadar lütfen bekleyiniz.</p></div>',
                confirm: null,
                backdropCloseDisabled: true,
            });
        }, 100);

        const result = await FOBBetslip.submit(save, await resolveCouponImage(imgData));
        clearTimeout(processingModalTimeout);

        if (!result) {
            setTimeout(() => {
                modalServiceSubject.next(false);
            }, 1500);
            return;
        }

        if (result.status === true && save === false) {
            try {
                // @ts-ignore
                fbq("track", "Purchase", {value: totalAmount, currency: "TRY"});
            } catch (e) {
            }
            FOBBetslip.clear();
            modalServiceSubject.next({
                case: "info",
                title: "Kuponunuz Oynandı",
                content: `<p class="text-center"><b>${result.data.coupon_data.approve_code
                }</b> kodu ile <b>${CurrencyFormat(Number(result.data.coupon_data.amount || 0))} ₺</b> tutarındaki <b>${BettingTypesMap[Number(result.data.coupon_data.bet_type)]?.name
                }</b> kuponunuz başarıyla oynandı. <br/>Kuponunuzun durumunu kuponlarım sayfasından takip edebilirsiniz.</p>`,
                confirm: {
                    sure: {label: "At Yarışı Kuponlarım", action: () => navigate(paths.memberTickets.url)},
                    cancel: {label: "Yeni Kupon Oyna", action: () => { if (isMobile) onClose?.(); }},
                },
            });
        } else if (result.status && save === true) {
            modalServiceSubject.next({
                case: "info",
                title: "Kuponunuz Kaydedildi",
                content: "Kuponunuz başarıyla Kaydedildi. Kuponunuzu kuponlarım sayfasında görüntüleyebilirsiniz",
                confirm: {
                    sure: {label: "Kayıtlı Kuponlarım", action: () => navigate(paths.memberSavedTickets.url)},
                    cancel: {label: "Yeni Kupon Oyna", action: () => { if (isMobile) onClose?.(); }},
                },
            });
        } else {
            let errorDescription: any = null;
            let errorMessageCode: any = null;
            if (result.data && result.data.error) {
                errorDescription = result.data.error.error_description;
                errorMessageCode = result.data.error.error_message;
            }

            let title: any = "Hata";
            let sure: any = null;
            if (typeof result.message === "string" && result.message.indexOf("Oturum") !== -1) {
                sure = {label: "Giriş Yap", action: () => navigate(paths.login.url)};
            }
            if (errorMessageCode === "TIMEOUT") {
                title = "Dikkat";
                sure = {label: "At Yarışı Kuponlarım", action: () => navigate(paths.memberTickets.url)};
            }

            modalServiceSubject.next({
                case: "danger",
                title: title,
                content: errorDescription || (typeof result.message === "object" ? JSON.stringify(result.message) : result.message),
                confirm: {sure, cancel: {label: "Tamam"}},
            });
        }
    };

    return (
        <div className="betslip" id={"betslip"}>
            {isMobile && (
                <button
                    className="btn btn-primary btn-icon float-start py-1 px-1 me-2"
                    onClick={() => onClose?.()}
                >
                    <img alt={"left"} src={iconLeft} className="invert-color me-1"/>
                </button>
            )}
            <h2 className="betting-type cursor-pointer">
                {slipItem.length > 1
                    ? 'Kombine Bahis'
                    : slipItem.length > 0
                        ? BettingTypesMap[Number(slipItem[0].bettingType)]?.name
                        : selectedBettingType?.name}
                {!isMobile && onClose && (
                    <img alt={"kapat"} src={iconClose} className="float-end cursor-pointer" onClick={() => onClose()}/>
                )}
            </h2>

            <div className="body betslip-body flex-column" id={"betslip-body"}>
                {slipItem.map((item: FobSlipItem, index: number) => {
                    const betTypePrefix = BettingTypesMap[item.bettingType]?.prefix;
                    const {isPair, primaryHorse, secondHorse} = resolveFobItemHorses(item);
                    const displayOdds = getFobItemDisplayOdds(item);
                    const isUnresolvable = !isFobItemPlayable(item);

                    return (
                        <div key={"fob-slip-item-" + index}>
                            <div
                                className={"card fob-slip-item mt-2"}
                                style={isUnresolvable ? {border: "1px solid var(--bs-danger)"} : undefined}
                            >
                                <div className={"fw-bold d-flex justify-content-between"}>
                                    {item.hippodrome.hippodrome} {item.run.no}. Koşu
                                    <div className={"mb-2 me-1"}>
                                        <img
                                            alt={"sil"}
                                            src={iconTrash}
                                            width={16}
                                            onClick={() => FOBBetslip.remove(item)}
                                            className="cursor-pointer float-right"
                                        />
                                    </div>
                                </div>
                                <div className={"card-body"}>
                                    {!isPair ? (
                                        <div className={"d-flex justify-content-between"}>
                                            <div className={"d-flex align-items-center"}>
                                                <div className={"me-1"}>
                                                    <img
                                                        src={`https://medya-cdn.tjk.org/formaftp/${primaryHorse?.owner?.code}.jpg`}
                                                        onError={({currentTarget}) => {
                                                            currentTarget.onerror = null;
                                                            currentTarget.src = jokeyIcon;
                                                        }}
                                                        width={21}
                                                        style={{borderRadius: "50%"}}
                                                        alt={primaryHorse?.jokey?.short_name}
                                                        className="jockey-uniform float-start "
                                                    />
                                                </div>
                                                <div className={"d-flex justify-content-between flex-column"}>
                                                    <div className={"d-flex align-items-center fw-bold"}>
                                                        <div>{primaryHorse?.no} - {primaryHorse?.name}</div>
                                                    </div>
                                                    <div className={"d-flex justify-content-between align-items-center"}>
                                                        <div>{primaryHorse?.jokey?.name}</div>
                                                    </div>
                                                </div>
                                            </div>
                                            <div className={"d-flex flex-row align-items-end"}>
                                                {betTypePrefix && (
                                                    <div className={"fw-bold odd-button selected"}>{betTypePrefix}</div>
                                                )}
                                                <div className={"fw-bold odd-button selected"}>{displayOdds}</div>
                                            </div>
                                        </div>
                                    ) : BettingTypesMap[item.bettingType]?.isHeadToHead ? (
                                        <div className={"d-flex justify-content-between"}>
                                            <div className={"d-flex align-items-center"}>
                                                <div className={"d-flex justify-content-between flex-column"}>
                                                    <div className={"d-flex align-items-center fw-bold"}>
                                                        <div>
                                                            {primaryHorse?.no} {primaryHorse?.name}
                                                            <span className={"fw-normal text-muted mx-1"}><br/>vs</span>
                                                            <span className={"fw-normal"}>{secondHorse?.no} {secondHorse?.name}</span>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                            <div className={"d-flex flex-row align-items-end"}>
                                                <div className={"fw-bold odd-button selected"}>{betTypePrefix}</div>
                                                <div className={"fw-bold odd-button selected"}>{displayOdds}</div>
                                            </div>
                                        </div>
                                    ) : (
                                        <div className={"d-flex justify-content-between"}>
                                            <div className={"d-flex align-items-center"}>
                                                <div className={"d-flex justify-content-between flex-column"}>
                                                    <div className={"d-flex align-items-center fw-bold"}>
                                                        <div>
                                                            {primaryHorse?.no} {primaryHorse?.name} - {secondHorse?.no} {secondHorse?.name}
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                            <div className={"d-flex flex-row align-items-end"}>
                                                <div className={"fw-bold odd-button selected"}>{betTypePrefix}</div>
                                                <div className={"fw-bold odd-button selected"}>{displayOdds}</div>
                                            </div>
                                        </div>
                                    )}
                                    {isUnresolvable && (
                                        <div className={"text-danger fs-7 fw-bold mt-1"}>
                                            Bu seçim artık geçerli değil, lütfen kaldırın
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    );
                })}

                {pendingPicks.map((pending, index: number) => {
                    const horseNo = pending.first ?? pending.second;
                    const horse = pending.run?.horseMap?.[String(horseNo)];
                    const isPendingStale = !isFobPendingPickPlayable(pending);

                    return (
                        <div key={"fob-pending-slip-" + index}>
                            <div
                                className={"card fob-slip-item mt-2"}
                                style={isPendingStale ? {border: "1px solid var(--bs-danger)"} : undefined}
                            >
                                <div className={"fw-bold d-flex justify-content-between"}>
                                    {pending.hippodrome.hippodrome} {pending.run.no}. Koşu
                                    <div className={"mb-2 me-1"}>
                                        <img
                                            alt={"sil"}
                                            src={iconTrash}
                                            width={16}
                                            onClick={() => FOBBetslip.clearPendingPick(pending.hippodrome, pending.run, pending.bettingType)}
                                            className="cursor-pointer float-right"
                                        />
                                    </div>
                                </div>
                                <div className={"card-body"}>
                                    <div className={"d-flex justify-content-between"}>
                                        <div className={"d-flex align-items-center"}>
                                            <div className={"me-1"}>
                                                <img
                                                    src={`https://medya-cdn.tjk.org/formaftp/${horse?.owner?.code}.jpg`}
                                                    onError={({currentTarget}) => {
                                                        currentTarget.onerror = null;
                                                        currentTarget.src = jokeyIcon;
                                                    }}
                                                    width={21}
                                                    style={{borderRadius: "50%"}}
                                                    alt={horse?.jokey?.short_name}
                                                    className="jockey-uniform float-start "
                                                />
                                            </div>
                                            <div className={"d-flex justify-content-between flex-column"}>
                                                <div className={"d-flex align-items-center fw-bold"}>
                                                    <div>{horse?.no} - {horse?.name}</div>
                                                </div>
                                                <div className={"d-flex justify-content-between align-items-center"}>
                                                    <div>{horse?.jokey?.name}</div>
                                                </div>
                                            </div>
                                        </div>
                                        <div className={"d-flex flex-row align-items-end"}>
                                            <div className={"text-primary fw-bold fs-7"}>Seçim Yapın</div>
                                        </div>
                                    </div>
                                    {isPendingStale && (
                                        <div className={"text-danger fs-7 fw-bold mt-1"}>
                                            Bu koşu artık kapalı, lütfen kaldırın
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    );
                })}

                <div className={"d-flex mt-2 justify-content-center align-items-center text-danger"}>
                    <img src={infoDangerIcon} className={"me-1"} alt={"info"}/>
                    <span className={"lh-1 fs-7 fw-medium"}>
                        Sabit ihtimalli bahis türünde oynanan kuponlar iptal edilemez.
                    </span>
                </div>

                {pendingPicks.length > 0 && (
                    <div className="w-100 p-2 alert alert-danger m-0">
                        Gerekli tüm koşu/alanlardan seçim yapmadınız.
                    </div>
                )}
            </div>

            <div className="footer">
                {systemOptions !== null && isNumber(systemOptions) && (
                    <div>
                        <label>Sistem</label>
                        <div className={"betslip-system"}>
                            {Array.from({length: systemOptions}).map((_el: any, index: number) => {
                                const value = index + 1;
                                const isSelected = options.fobSystemSelected.includes(value);
                                return (
                                    <div
                                        onClick={() => FOBBetslip.toggleSystemOption(value)}
                                        className={"betslip-system-item" + (isSelected ? " selected" : "")}
                                        key={"system-option" + index}
                                    >
                                        {value}
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                )}

                <div>
                    <label>Misli:</label>
                    <div className={"betslip-misli"}>
                        <input
                            type="number"
                            className={"betslip-input"}
                            style={{textAlign: "left"}}
                            pattern="^[0-9\b]+$"
                            min={1}
                            max={BET_SLIP_MAX_MULTIPLY}
                            step={1}
                            value={options.multiply || ""}
                            onKeyDown={preventBetSlipValues}
                            onChange={(e) => FOBBetslip.setOptions({multiply: Number(e.target.value)})}
                            onBlur={() => {
                                if (Number(options.multiply) > BET_SLIP_MAX_MULTIPLY) {
                                    FOBBetslip.setOptions({multiply: BET_SLIP_MAX_MULTIPLY});
                                } else if (!options.multiply) {
                                    FOBBetslip.setOptions({multiply: 20});
                                }
                            }}
                            onPaste={(e) => {
                                e.preventDefault();
                                return false;
                            }}
                        />
                        <select
                            className="betslip-select"
                            value={options.multiply}
                            onChange={(e) => FOBBetslip.setOptions({multiply: Number(e.target.value)})}
                        >
                            {multiplyChoices.map((el: number, key: number) => (
                                <option key={key} value={el}>
                                    {el}
                                </option>
                            ))}
                        </select>
                    </div>
                </div>

                <div>
                    <label>Tutar:</label>
                    <span>{CurrencyFormat(totalAmount)}₺</span>
                </div>

                {options.fobSystemSelected.length > 0 && hasKnownOdds && minWin !== maxWin && (
                    <div>
                        <label>Min. Kazanç:</label>
                        <span>{CurrencyFormat(minWin)}₺</span>
                    </div>
                )}

                <div>
                    <label>Maks. Kazanç:</label>
                    <span>{!hasKnownOdds ? "KO" : `${CurrencyFormat(maxWin)}₺${isClosing ? " + KO" : ""}`}</span>
                </div>

                {auth.authentication && auth.member && auth.member.id ? (
                    <div>
                        <label>Puan Kullan:</label>
                        <span>
                            <input
                                type="checkbox"
                                checked={options.usePointBalance}
                                disabled={auth.member ? auth.member.balance_point <= 0 : true}
                                onChange={() => FOBBetslip.setOptions({usePointBalance: !options.usePointBalance})}
                            />{" "}
                            {auth.member ? auth.member.balance_point + "₺" : "0.00₺"}
                        </span>
                    </div>
                ) : null}

                {options.errorMessage ? (
                    <div className="mt-1 mb-1">
                        <div className="w-100 p-2 alert alert-danger m-0">{options.errorMessage}</div>
                    </div>
                ) : null}

                {auth.authentication && auth.member && auth.member.is_editor === true && (
                    <div>
                        <label>Paylaş:</label>
                        <span>
                            <input
                                type="checkbox"
                                checked={options.shareCoupon}
                                onChange={() => FOBBetslip.setOptions({shareCoupon: !options.shareCoupon})}
                            />
                        </span>
                    </div>
                )}

                <div className={"d-flex"}>
                    <span className={"lh-1 fs-7 fw-medium text-danger w-100"} style={{textAlign: "left"}}>
                        Bilet oynanırken tüm oranların değişebileceğini kabul et
                    </span>
                    <input
                        type="checkbox"
                        className={"ms-2"}
                        checked={options.acceptChangedOdds}
                        onChange={() => FOBBetslip.setOptions({acceptChangedOdds: !options.acceptChangedOdds})}
                    />
                </div>

                {options.inProgress ? (
                    <div className="actions">
                        <button className="button-clear" disabled>
                            <img src={iconTrash} alt=""/>
                        </button>
                        <button disabled className="button-save">
                            <img src={iconSave} alt=""/>
                        </button>
                        <button className="button-play" disabled>
                            Hemen Oyna
                        </button>
                    </div>
                ) : (
                    <div className="actions">
                        <button className="button-clear">
                            <img src={iconTrash} onClick={() => FOBBetslip.clear()} alt=""/>
                        </button>

                        <button onClick={() => playCoupon(true)} className="button-save" disabled={slipItem.length === 0}>
                            <img src={iconSave} alt=""/>
                        </button>

                        {!options.shareCoupon && (allHippodromesOpen || pendingPicks.length > 0) && (
                            <button
                                disabled={!options.acceptChangedOdds || options.inProgress || !!options.errorMessage || pendingPicks.length > 0 || hasStaleItem}
                                className={`button-play`}
                                onClick={() => confirmCoupon()}
                            >
                                Hemen Oyna
                            </button>
                        )}

                        {slipItem.length > 0 && !allHippodromesOpen && (
                            <button className="button-play" disabled>
                                Bahisler Açılmadı
                            </button>
                        )}

                        {options.shareCoupon && (
                            <button
                                disabled={slipItem.length === 0 || !options.acceptChangedOdds || !!options.errorMessage || pendingPicks.length > 0 || hasStaleItem}
                                className={`button-play share`}
                                onClick={() => confirmCoupon()}
                            >
                                Hemen Paylaş
                            </button>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
}

export function NewFobBetslipToggleTab({onOpen}: { onOpen: () => void }) {
    const slipItem = useFobBetslip();
    const totalOdds = FOBBetslip.getTotalOdds();

    return (
        <div onClick={onOpen} className={"fob-toggle py-1"}>
            <div className="d-flex align-items-center justify-content-start ">
                <span className={`fob-toggle-icon`}>
                    <span className="horse-amount">{slipItem.length}</span>
                    <img src={blackHorse} alt=""/>
                    <span className="up-icon">
                        <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="30"
                            height="30"
                            fill="currentColor"
                            className="bi bi-caret-up"
                            viewBox="0 0 16 16"
                        >
                            <path
                                d="M3.204 11h9.592L8 5.519zm-.753-.659 4.796-5.48a1 1 0 0 1 1.506 0l4.796 5.48c.566.647.106 1.659-.753 1.659H3.204a1 1 0 0 1-.753-1.659"/>
                        </svg>
                    </span>
                </span>
                <div className="d-block justify-content-start ms-0">
                    <span className="fw-light">Oran:</span>
                    <span className="nowrap">{slipItem.length === 0 ? "-" : totalOdds === 'KO' ? 'KO' : `${Number(totalOdds).toFixed(2)}${FOBBetslip.hasClosingOdds() ? ' + KO' : ''}`}</span>
                </div>
            </div>
        </div>
    );
}

export function NewMobileFobBetslipBar({onOpen}: { onOpen: () => void }) {
    const slipItem = useFobBetslip();
    const totalOdds = FOBBetslip.getTotalOdds();
    const totalAmount = FOBBetslip.getTotalAmount();

    return (
        <div id="mobile-foot-betslip" className="mobile-foot-betslip sticky">
            <div className="row">
                <div className="col-4">
                    <h2 className="mb-0 fob-betslip-header">BİLETİM</h2>
                </div>
                <div className="col-8 text-end">
                    <button className="btn btn-primary float-end ms-1 fw-bold" onClick={onOpen}>
                        OYNA
                    </button>
                    ORAN: {slipItem.length === 0 ? "-" : totalOdds === 'KO' ? 'KO' : `${Number(totalOdds).toFixed(2)}${FOBBetslip.hasClosingOdds() ? ' + KO' : ''}`}
                    <span className="total d-block fw-bold">TUTAR: {CurrencyFormat(totalAmount)}₺</span>
                </div>
            </div>

            <div className="row">
                <div className="d-flex py-2 pt-0 w-100 fw-bold fs-6">
                    <div className={"d-flex"} style={{fontSize: "14px !important"}}>
                        {slipItem.map((item: FobSlipItem, index: number) => {
                            const {isPair, primaryHorse, label} = resolveFobItemHorses(item);
                            const isSuffixed = !!BettingTypesMap[item.bettingType]?.suffix;
                            const isLast = index === slipItem.length - 1;

                            if (!isPair && !isSuffixed && slipItem.length === 1) {
                                return (
                                    <div key={"mobile-fob-slip-" + index} className={"d-flex align-items-center"}>
                                        <i className={"d-flex align-content-center"}>
                                            <img
                                                alt={"sil"}
                                                src={iconTrash}
                                                width={16}
                                                onClick={() => FOBBetslip.remove(item)}
                                                className="cursor-pointer float-right me-2"
                                            />
                                        </i>
                                        {primaryHorse?.no} - {primaryHorse?.name}
                                    </div>
                                );
                            }

                            return (
                                <div key={"mobile-fob-slip-" + index} className={"d-flex align-items-center"}>
                                    {label}{isLast ? '' : ','}&nbsp;
                                </div>
                            );
                        })}
                    </div>
                </div>
            </div>
        </div>
    );
}
